<template>
    <div
        class="notification-container"
        :class="[
            variant && `variant_${variant}`,
            background && `background_${background}`,
            columnMobile && 'break',
            padding && `padding_${padding}`,
            `tier-${tier}`,
        ]">
        <div
            v-if="icon || asset"
            class="notification-icon"
            :class="[iconPosition && `iconPosition_${iconPosition}`]">
            <Icon v-if="icon" :type="icon" size="s" v-tippy="iconTooltip" />
            <Asset v-else-if="asset" :type="asset" class="asset" />
        </div>

        <div class="notification-content">
            <div v-if="showHeader" class="notification-content-header">
                <div class="notification-content-header-content">
                    <div v-if="title" class="notification-content-header-content-title">
                        {{ title }}
                    </div>
                    <slot name="title" />
                    <ButtonLink v-if="linkLabel" size="m" :onClick="onLinkClick">
                        {{ linkLabel }}
                    </ButtonLink>
                </div>
                <Button
                    v-if="buttonLabel"
                    class="notification-content-header-button"
                    color="black"
                    variant="border"
                    size="s"
                    :onClick="onButtonClick">
                    {{ buttonLabel }}
                </Button>
            </div>
            <slot />
        </div>
    </div>
</template>

<script lang="ts">
    import { PropType, defineComponent, computed, useSlots } from 'vue';
    import { Type as IconType } from '@ui/components/Icon/Icon.interfaces';
    import { Type as AssetType } from '@ui/components/Asset/Asset.interfaces';
    import {
        Variant,
        Background,
        Padding,
        IconPosition,
        Tier,
    } from './Notification.interfaces';
    import Icon from '@ui/components/Icon/Icon.vue';
    import Asset from '../Asset/Asset.vue';
    import ButtonLink from '../Button/ButtonLink.vue';
    import Button from '../Button/Button.vue';

    export default defineComponent({
        name: 'Notification',
        components: { Icon, Asset, ButtonLink, Button },
        props: {
            title: {
                type: String as PropType<string | undefined>,
                default: '',
            },
            linkLabel: {
                type: String as PropType<string | undefined>,
                default: '',
            },
            onLinkClick: {
                type: Function,
            },
            onButtonClick: {
                type: Function,
            },
            columnMobile: {
                type: Boolean,
                default: false,
            },
            variant: {
                type: String as PropType<Variant>,
                default: 'neutral',
            },
            icon: {
                type: String as PropType<IconType | null>,
                default: null,
            },
            iconTooltip: {
                type: String as PropType<string | null>,
                default: '',
            },
            iconPosition: {
                type: String as PropType<IconPosition | null>,
                default: 'start',
            },
            background: {
                type: String as PropType<Background | null>,
                default: null,
            },
            padding: {
                type: String as PropType<Padding | null>,
                default: 's',
            },
            asset: {
                type: String as PropType<AssetType | null>,
                default: null,
            },
            buttonLabel: {
                type: String,
                default: '',
            },
            tier: {
                type: Number as PropType<Tier>,
                default: 2,
            },
        },
        setup(props) {
            const slots = useSlots();
            const showHeader = computed(
                () => !!props.title || !!props.linkLabel || !!slots.title
            );

            return {
                showHeader,
            };
        },
    });
</script>

<style lang="scss">
    @forward './Notification.scss';
</style>
